// src/pages/PrivacyPolicyPage.js
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const PageContainer = styled.div`
  padding-bottom: 5rem;
`;

const HeaderSection = styled.section`
  text-align: center;
  padding: 5rem 10% 4rem 10%;
`;

const PageTitle = styled(motion.h1)`
  font-size: 3.5rem;
  margin-bottom: 1rem;
`;

const PageSubtitle = styled(motion.p)`
  font-size: 1.3rem;
  color: var(--text-secondary-color);
`;

// --- YAHAN SE LAYOUT CHANGES SHURU HOTE HAIN ---

// Yeh ab sirf vertical padding dega
const ContentSection = styled.section`
  padding: 0 10% 5rem 10%;
`;

// Naya container jo text ko wrap karega
const PolicyContainer = styled.div`
  max-width: 1100px; /* Text area ko chora kiya hai */
  margin: 0 auto;
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 3rem 4rem; /* Andar se behtar padding */
  text-align: left;
  color: var(--text-secondary-color);

  @media (max-width: 768px) {
    padding: 2rem; /* Mobile par padding kam karein */
  }
`;

const PolicyHeading = styled.h2`
  font-size: 2rem;
  color: var(--text-color);
  margin-top: 3rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid var(--border-color);
  padding-bottom: 0.75rem;

  &:first-child {
    margin-top: 0; /* Pehli heading se extra space hatayein */
  }
`;

const PolicyText = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 1.5rem;
`;

const PolicyList = styled.ul`
  padding-left: 25px;
  font-size: 1.1rem;
  line-height: 1.8;
  
  li {
    margin-bottom: 1rem;
  }
`;

const StyledLink = styled.a`
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    text-decoration: underline;
    opacity: 0.8;
  }
`;

const PrivacyPolicyPage = () => {
    return (
        <PageContainer>
            <Helmet>
                <title>Privacy Policy for Trend Genie App</title>
                <meta name="description" content="Privacy Policy for the Trend Genie Android application, detailing the data we collect and how we use it to provide our services." />
                <link rel="canonical" href="https://www.trendgenie.io/privacy-policy" />
            </Helmet>

            <HeaderSection>
                <PageTitle initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                    Privacy Policy for Trend Genie App
                </PageTitle>
                <PageSubtitle initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                    Last Updated: November 13, 2025
                </PageSubtitle>
            </HeaderSection>

            <ContentSection>
                <PolicyText>
                    Trend Genie is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and share information about you when you use our mobile application, Trend Genie.
                </PolicyText>

                <PolicyHeading>Information We Collect</PolicyHeading>
                <PolicyText>
                    We collect the following types of information:
                </PolicyText>
                <PolicyList>
                    <li><strong>Information You Provide to Us:</strong> When you create an account, we may collect your name, email address, and password.</li>
                    <li><strong>Usage Information:</strong> We collect information about how you use the App, such as the features you use, the assets you track in your watchlist, and the actions you take. This helps us improve the app experience.</li>
                    <li><strong>Device Information:</strong> We collect information about the mobile device you use to access our App, including the hardware model, operating system and version, and unique device identifiers.</li>
                </PolicyList>

                <PolicyHeading>How We Use Your Information</PolicyHeading>
                <PolicyText>
                    We use the information we collect to:
                </PolicyText>
                 <PolicyList>
                    <li>Provide, maintain, and improve our App and its features.</li>
                    <li>Personalize your experience within the App.</li>
                    <li>Process transactions, such as your subscription to our Premium Plan.</li>
                    <li>Communicate with you, including sending you technical notices, updates, and support messages.</li>
                    <li>Monitor and analyze trends, usage, and activities in connection with our App.</li>
                </PolicyList>
                
                <PolicyHeading>Third-Party Services</PolicyHeading>
                <PolicyText>
                    Our App uses third-party services that may collect information used to identify you. Below are links to the privacy policies of the third-party service providers used by the App:
                </PolicyText>
                 <PolicyList>
                    <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{color: 'var(--primary-color)'}}>Google Play Services</a></li>
                    <li><a href="https://firebase.google.com/policies/analytics" target="_blank" rel="noopener noreferrer" style={{color: 'var(--primary-color)'}}>Google Analytics for Firebase</a></li>
                    {<li><a href="https://support.google.com/admob/answer/6128543" target="_blank" rel="noopener noreferrer" style={{color: 'var(--primary-color)'}}>AdMob</a></li>}
                </PolicyList>

                <PolicyHeading>Data Security</PolicyHeading>
                <PolicyText>
                    We take reasonable measures to help protect information about you from loss, theft, misuse, and unauthorized access, disclosure, alteration, and destruction.
                </PolicyText>
                
                <PolicyHeading>Your Choices</PolicyHeading>
                <PolicyText>
                    You may update, correct, or delete information about you at any time by logging into your in-app account. If you wish to delete your account, please contact us at support@trendgenie.io, but note that we may retain certain information as required by law or for legitimate business purposes.
                </PolicyText>
                
                <PolicyHeading>Children's Privacy</PolicyHeading>
                <PolicyText>
                    Our service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from children under 13. If we discover that a child under 13 has provided us with personal information, we immediately delete this from our servers.
                </PolicyText>

                <PolicyHeading>Changes to This Privacy Policy</PolicyHeading>
                <PolicyText>
                    We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page. You are advised to review this Privacy Policy periodically for any changes.
                </PolicyText>

                <PolicyHeading>Contact Us</PolicyHeading>
                <PolicyText>
                    If you have any questions about this Privacy Policy, please contact us at: support@trendgenie.io
                </PolicyText>
            </ContentSection>
        </PageContainer>
    );
};

export default PrivacyPolicyPage;